// @ts-nocheck
import React, { useEffect, useMemo, useState, Suspense, lazy } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Editor as MailyEditor } from "@maily-to/core";
import { render as mailyRender } from "@maily-to/render";
import { getSessionUser } from '@/lib/authSession';
// Maily's bundled CSS uses `@layer base` which conflicts with Tailwind's
// PostCSS pipeline. Serve a pre-copied copy from /public to bypass PostCSS.
if (typeof document !== "undefined" && !document.getElementById("maily-stylesheet")) {
  const link = document.createElement("link");
  link.id = "maily-stylesheet";
  link.rel = "stylesheet";
  link.href = "/vendor/maily.css";
  document.head.appendChild(link);
}

// ---------- helpers ----------
function renderVariables(text: string, variables: any[]) {
  let out = text || "";
  for (const v of variables) {
    const re = new RegExp(`{{\\s*${v.key}\\s*}}`, "g");
    out = out.replace(re, v.example_value || `[${v.key}]`);
  }
  return out;
}

function stripHtml(html: string) {
  if (typeof document !== "undefined") {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || "";
  }
  return (html || "").replace(/<[^>]+>/g, "");
}

const DEFAULT_EDITOR_DOC = {
  type: "doc",
  content: [{ type: "paragraph", content: [{ type: "text", text: "" }] }],
};

// ---------- Banner picker (reuses ad_assets) ----------
function BannerPicker({ value, onChange, label = "None" }: any) {
  const { data: assets = [] } = useQuery({
    queryKey: ["ad-assets-for-email"],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("ad_assets")
        .select("id, name, advertiser_name, image_url")
        .eq("is_active", true)
        .order("name");
      return data || [];
    },
  });

  const selected = assets.find((a: any) => a.id === value);

  return (
    <div>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-sm text-ink-primary"
      >
        <option value="">{label}</option>
        {assets.map((a: any) => (
          <option key={a.id} value={a.id}>
            {a.name}
            {a.advertiser_name ? ` — ${a.advertiser_name}` : ""}
          </option>
        ))}
      </select>
      {selected?.image_url && (
        <img
          src={selected.image_url}
          alt={selected.name}
          className="mt-2 w-full max-h-20 object-contain rounded-token-sm border border-stroke-subtle"
        />
      )}
      <Link
        to="/admin/ad-assets"
        className="text-caption text-cyan hover:text-cyan-hover mt-1 inline-block"
      >
        Manage ad assets →
      </Link>
    </div>
  );
}

// ---------- Compose rail ----------
function ComposeRail({
  headerId,
  footerId,
  topBannerId,
  bottomBannerId,
  inlineBannerId,
  onChange,
}: any) {
  const { data: headers = [] } = useQuery({
    queryKey: ["message-email-headers"],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("message_email_headers")
        .select("id, name, is_default")
        .eq("is_active", true)
        .order("sort_order");
      return data || [];
    },
  });
  const { data: footers = [] } = useQuery({
    queryKey: ["message-email-footers"],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("message_email_footers")
        .select("id, name, is_default")
        .eq("is_active", true)
        .order("sort_order");
      return data || [];
    },
  });

  return (
    <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-5 space-y-5">
      <div>
        <h3 className="text-heading-4 text-ink-primary">Composition</h3>
        <p className="text-caption text-ink-tertiary">
          Wrap your content with reusable pieces.
        </p>
      </div>

      <div>
        <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
          Header
        </label>
        <select
          value={headerId || ""}
          onChange={(e) => onChange("header_id", e.target.value || null)}
          className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-sm text-ink-primary"
        >
          <option value="">None</option>
          {headers.map((h: any) => (
            <option key={h.id} value={h.id}>
              {h.name}
              {h.is_default ? " (default)" : ""}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
          Top banner ad
        </label>
        <BannerPicker
          value={topBannerId}
          onChange={(v: any) => onChange("top_banner_asset_id", v)}
        />
      </div>

      <div>
        <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
          Inline banner ad (mid-body)
        </label>
        <BannerPicker
          value={inlineBannerId}
          onChange={(v: any) => onChange("inline_banner_asset_id", v)}
        />
        <p className="text-caption text-ink-tertiary mt-1">
          Splice into the body using <code>{`{{banner_placeholder}}`}</code>.
        </p>
      </div>

      <div>
        <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
          Bottom banner ad
        </label>
        <BannerPicker
          value={bottomBannerId}
          onChange={(v: any) => onChange("bottom_banner_asset_id", v)}
        />
      </div>

      <div>
        <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
          Footer
        </label>
        <select
          value={footerId || ""}
          onChange={(e) => onChange("footer_id", e.target.value || null)}
          className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-sm text-ink-primary"
        >
          <option value="">None</option>
          {footers.map((f: any) => (
            <option key={f.id} value={f.id}>
              {f.name}
              {f.is_default ? " (default)" : ""}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ---------- Triggers panel ----------
function TriggersPanel({ template }: any) {
  const triggers = template?.trigger_events || [];
  return (
    <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-5 space-y-3">
      <div>
        <h3 className="text-heading-4 text-ink-primary">Triggers</h3>
        <p className="text-caption text-ink-tertiary">
          When this template fires automatically.
        </p>
      </div>
      {triggers.length === 0 ? (
        <p className="text-body-sm text-ink-tertiary">
          No automatic triggers. This template is operator-fired only (Messages broadcast).
        </p>
      ) : (
        <div className="space-y-2">
          {triggers.map((t: any, idx: number) => (
            <div
              key={idx}
              className="bg-surface-page border border-stroke-subtle rounded-token-md p-3"
            >
              <p className="text-body-sm text-ink-primary">{t.description}</p>
              <p className="text-caption text-ink-tertiary mt-1 font-mono">{t.source}</p>
            </div>
          ))}
        </div>
      )}
      <p className="text-caption text-ink-tertiary border-t border-stroke-subtle pt-3">
        Triggers are set by developers. To add a trigger to this template, ask Nancy.
      </p>
    </div>
  );
}

// ---------- Email editor ----------
function EmailChannelEditor({ template, channel, variables }: any) {
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState(channel.subject || "");
  const [editorState, setEditorState] = useState<any>(
    channel.editor_state || DEFAULT_EDITOR_DOC,
  );
  const [editorInstance, setEditorInstance] = useState<any>(null);
  const [isEnabled, setIsEnabled] = useState(channel.is_enabled);
  const [headerId, setHeaderId] = useState<string | null>(channel.header_id || null);
  const [footerId, setFooterId] = useState<string | null>(channel.footer_id || null);
  const [topBannerId, setTopBannerId] = useState<string | null>(
    channel.top_banner_asset_id || null,
  );
  const [bottomBannerId, setBottomBannerId] = useState<string | null>(
    channel.bottom_banner_asset_id || null,
  );
  const [inlineBannerId, setInlineBannerId] = useState<string | null>(
    channel.inline_banner_asset_id || null,
  );
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setSubject(channel.subject || "");
    setEditorState(channel.editor_state || DEFAULT_EDITOR_DOC);
    setIsEnabled(channel.is_enabled);
    setHeaderId(channel.header_id || null);
    setFooterId(channel.footer_id || null);
    setTopBannerId(channel.top_banner_asset_id || null);
    setBottomBannerId(channel.bottom_banner_asset_id || null);
    setInlineBannerId(channel.inline_banner_asset_id || null);
    setDirty(false);
  }, [channel.id]);

  const markDirty = () => setDirty(true);

  const handleComposeChange = (field: string, value: any) => {
    if (field === "header_id") setHeaderId(value);
    else if (field === "footer_id") setFooterId(value);
    else if (field === "top_banner_asset_id") setTopBannerId(value);
    else if (field === "bottom_banner_asset_id") setBottomBannerId(value);
    else if (field === "inline_banner_asset_id") setInlineBannerId(value);
    markDirty();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Render Maily JSON to HTML
      const currentJson = editorInstance ? editorInstance.getJSON() : editorState;
      const renderedHtml = await mailyRender(currentJson);

      await (supabase as any).from("message_template_revisions").insert({
        channel_id: channel.id,
        version: channel.version,
        snapshot: {
          subject: channel.subject,
          body_html: channel.body_html,
          body_text: channel.body_text,
          editor_state: channel.editor_state,
          is_enabled: channel.is_enabled,
        },
      });

      const { error: updateErr } = await (supabase as any)
        .from("message_template_channels")
        .update({
          subject,
          body_html: renderedHtml,
          body_text: stripHtml(renderedHtml),
          editor_state: currentJson,
          header_id: headerId,
          footer_id: footerId,
          top_banner_asset_id: topBannerId,
          bottom_banner_asset_id: bottomBannerId,
          inline_banner_asset_id: inlineBannerId,
          is_enabled: isEnabled,
          version: (channel.version || 1) + 1,
          last_modified_at: new Date().toISOString(),
        })
        .eq("id", channel.id);
      if (updateErr) throw updateErr;

      queryClient.invalidateQueries({ queryKey: ["message-template", template.id] });
      queryClient.invalidateQueries({ queryKey: ["message-templates"] });
      setDirty(false);
      toast.success("Template saved");
    } catch (err: any) {
      toast.error("Save failed: " + (err?.message || "unknown"));
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!subject || !subject.trim()) {
      toast.error("Subject is required. Fill it in before sending a test.");
      return;
    }
    // Render the CURRENT editor state to HTML so unsaved edits are tested
    let renderedHtml = "";
    try {
      const currentJson = editorInstance ? editorInstance.getJSON() : editorState;
      renderedHtml = await mailyRender(currentJson);
    } catch (e: any) {
      toast.error("Failed to render email body: " + (e?.message || "unknown"));
      return;
    }
    if (!renderedHtml || !renderedHtml.replace(/<[^>]*>/g, "").trim()) {
      toast.error("Email body is empty. Add content before sending a test.");
      return;
    }
    const { data: { user } } = await getSessionUser();
    if (!user?.email) {
      toast.error("No email on logged-in user");
      return;
    }
    const { data, error } = await (supabase as any).functions.invoke("send-system-message", {
      body: {
        template_key: template.key,
        channels: ["email"],
        recipient: { email: user.email, user_id: user.id, name: "Test Recipient" },
        context: Object.fromEntries(
          (variables || []).map((v: any) => [v.key, v.example_value || `[${v.key}]`]),
        ),
        dry_run: false,
        // Pass current form state so unsaved changes are reflected
        override_subject: subject,
        override_body_html: renderedHtml,
        override_header_id: headerId,
        override_footer_id: footerId,
        override_top_banner_asset_id: topBannerId,
        override_bottom_banner_asset_id: bottomBannerId,
        override_inline_banner_asset_id: inlineBannerId,
      },
    });
    if (error) {
      toast.error("Test send failed: " + (error.message || "unknown"));
      return;
    }
    const sendIds: string[] = data?.send_ids || [];
    if (sendIds.length === 0) {
      toast.error("Send returned no message_send rows — template may not be enabled");
      return;
    }
    await new Promise((r) => setTimeout(r, 1500));
    const { data: rows } = await (supabase as any)
      .from("message_sends")
      .select("status, error_message, recipient_email")
      .in("id", sendIds);
    const failed = (rows || []).filter((r: any) => r.status === "failed");
    if (failed.length > 0) {
      toast.error(`Send failed: ${failed[0].error_message || "unknown error"}`);
      return;
    }
    toast.success(`Test sent to ${user.email}`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      <div className="space-y-4 min-w-0">
        <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-5">
          <label className="flex items-center gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => {
                setIsEnabled(e.target.checked);
                markDirty();
              }}
            />
            <span className="text-body-md text-ink-primary font-medium">
              Email channel enabled
            </span>
          </label>

          <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
            Subject line
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              markDirty();
            }}
            placeholder="e.g. Welcome to {{event_name}}"
            className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-md text-ink-primary mb-4"
          />

          <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
            Email body
          </label>
          <div className="border border-stroke-subtle rounded-token-md bg-white text-black overflow-hidden maily-editor-shell">
            <MailyEditor
              key={channel.id}
              contentJson={editorState}
              onCreate={(editor: any) => setEditorInstance(editor)}
              onUpdate={(editor: any) => {
                setEditorState(editor.getJSON());
                markDirty();
              }}
              config={{ hasMenuBar: true, spellCheck: true }}
            />
          </div>
          <p className="text-caption text-ink-tertiary mt-2">
            Use the slash command (<code>/</code>) for blocks. Insert{" "}
            <code>{`{{variable_name}}`}</code> as plain text. Use{" "}
            <code>{`{{banner_placeholder}}`}</code> where the inline banner should appear.
          </p>
        </div>

        <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-5">
          <h3 className="text-heading-4 text-ink-primary mb-2">Available Variables</h3>
          <p className="text-body-sm text-ink-tertiary mb-3">
            Type <code>{`{{key}}`}</code> in the editor or copy from below.
          </p>
          {(!variables || variables.length === 0) ? (
            <p className="text-body-sm text-ink-tertiary">
              No variables declared for this template.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {variables.map((v: any) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(`{{${v.key}}}`);
                    toast.success(`Copied {{${v.key}}}`);
                  }}
                  className="bg-cyan-subtle text-cyan border border-cyan/30 rounded-token-sm px-2.5 py-1 text-caption font-mono hover:bg-cyan hover:text-cyan-foreground transition-colors"
                  title={v.description}
                >
                  {`{{${v.key}}}`}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-2 pt-2">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleSendTest}
              className="bg-surface-card border border-stroke-subtle hover:border-cyan rounded-token-md px-4 py-2 text-body-sm text-ink-primary hover:text-cyan transition-colors"
            >
              Send test to me
            </button>
            <div className="flex items-center gap-3">
              {dirty && <span className="text-caption text-status-warning">Unsaved changes</span>}
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || saving}
                className="bg-cyan text-cyan-foreground hover:bg-cyan-hover disabled:opacity-50 px-5 py-2 rounded-token-md text-body-sm font-medium transition-colors"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
          {dirty && (
            <p className="text-caption text-ink-tertiary">
              ℹ Test sends use the current form state, not the saved template. Save before triggering real workflows that use this template.
            </p>
          )}
        </div>
      </div>

      <div className="lg:sticky lg:top-6 lg:self-start space-y-4">
        <ComposeRail
          headerId={headerId}
          footerId={footerId}
          topBannerId={topBannerId}
          bottomBannerId={bottomBannerId}
          inlineBannerId={inlineBannerId}
          onChange={handleComposeChange}
        />
        <TriggersPanel template={template} />
      </div>
    </div>
  );
}


// ---------- SMS editor ----------
function SmsChannelEditor({ template, channel, variables }: any) {
  const queryClient = useQueryClient();
  const [smsBody, setSmsBody] = useState(channel.sms_body || "");
  const [isEnabled, setIsEnabled] = useState(channel.is_enabled);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSmsBody(channel.sms_body || "");
    setIsEnabled(channel.is_enabled);
    setDirty(false);
  }, [channel.id]);

  const segments = Math.ceil((smsBody.length || 1) / 160);

  const handleSave = async () => {
    setSaving(true);
    try {
      await (supabase as any).from("message_template_revisions").insert({
        channel_id: channel.id,
        version: channel.version,
        snapshot: { sms_body: channel.sms_body, is_enabled: channel.is_enabled },
      });
      const { error } = await (supabase as any)
        .from("message_template_channels")
        .update({
          sms_body: smsBody,
          is_enabled: isEnabled,
          version: (channel.version || 1) + 1,
          last_modified_at: new Date().toISOString(),
        })
        .eq("id", channel.id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["message-template", template.id] });
      queryClient.invalidateQueries({ queryKey: ["message-templates"] });
      setDirty(false);
      toast.success("SMS template saved");
    } catch (err: any) {
      toast.error("Save failed: " + (err?.message || "unknown"));
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!smsBody || !smsBody.trim()) {
      toast.error("SMS message is empty. Add content before sending a test.");
      return;
    }
    const { data: { user } } = await getSessionUser();
    if (!user) {
      toast.error("Not logged in");
      return;
    }
    const { data: phone } = await (supabase as any)
      .from("user_phone_numbers")
      .select("phone_e164")
      .eq("user_id", user.id)
      .eq("is_verified", true)
      .eq("is_primary", true)
      .maybeSingle();
    if (!phone?.phone_e164) {
      toast.error("Add and verify a phone number to your profile first");
      return;
    }
    const { data, error } = await (supabase as any).functions.invoke("send-system-message", {
      body: {
        template_key: template.key,
        channels: ["sms"],
        recipient: { phone: phone.phone_e164, user_id: user.id },
        context: Object.fromEntries(
          (variables || []).map((v: any) => [v.key, v.example_value || `[${v.key}]`]),
        ),
        override_sms_body: smsBody,
      },
    });
    if (error) {
      toast.error("Test SMS failed: " + (error.message || "unknown"));
      return;
    }
    const sendIds: string[] = data?.send_ids || [];
    if (sendIds.length > 0) {
      await new Promise((r) => setTimeout(r, 1500));
      const { data: rows } = await (supabase as any)
        .from("message_sends")
        .select("status, error_message")
        .in("id", sendIds);
      const failed = (rows || []).filter((r: any) => r.status === "failed");
      if (failed.length > 0) {
        toast.error(`Send failed: ${failed[0].error_message || "unknown error"}`);
        return;
      }
    }
    toast.success(`Test SMS sent to ${phone.phone_e164}`);
  };

  return (
    <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-5 max-w-2xl">
      <label className="flex items-center gap-3 cursor-pointer mb-5">
        <input
          type="checkbox"
          checked={isEnabled}
          onChange={(e) => {
            setIsEnabled(e.target.checked);
            setDirty(true);
          }}
        />
        <span className="text-body-md text-ink-primary font-medium">SMS channel enabled</span>
      </label>

      <label className="block text-body-sm text-ink-primary font-medium mb-1.5">SMS message</label>
      <textarea
        value={smsBody}
        onChange={(e) => {
          setSmsBody(e.target.value);
          setDirty(true);
        }}
        rows={4}
        maxLength={1600}
        placeholder="Weather delay: {{event_name}} games today are delayed until {{new_start_time}}."
        className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-md text-ink-primary"
      />
      <div className="flex items-center justify-between mt-2">
        <p className="text-caption text-ink-tertiary">
          {smsBody.length} chars · {segments} segment{segments === 1 ? "" : "s"}
          {segments > 1 && (
            <span className="text-status-warning ml-2">⚠ Long messages cost more per send</span>
          )}
        </p>
        {variables && variables.length > 0 && (
          <div className="flex flex-wrap gap-1 justify-end">
            {variables.slice(0, 4).map((v: any) => (
              <button
                key={v.id}
                type="button"
                onClick={() => {
                  setSmsBody((prev) => prev + `{{${v.key}}}`);
                  setDirty(true);
                }}
                className="bg-cyan-subtle text-cyan border border-cyan/30 rounded-token-sm px-2 py-0.5 text-caption font-mono hover:bg-cyan hover:text-cyan-foreground transition-colors"
              >
                {`{{${v.key}}}`}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-6 pt-4 border-t border-stroke-subtle">
        <button
          type="button"
          onClick={handleSendTest}
          className="bg-surface-card border border-stroke-subtle hover:border-cyan rounded-token-md px-4 py-2 text-body-sm text-ink-primary hover:text-cyan transition-colors"
        >
          Send test SMS to me
        </button>
        <div className="flex items-center gap-3">
          {dirty && <span className="text-caption text-status-warning">Unsaved</span>}
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            className="bg-cyan text-cyan-foreground hover:bg-cyan-hover disabled:opacity-50 px-5 py-2 rounded-token-md text-body-sm font-medium transition-colors"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- New Template Form ----------
function NewTemplateForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("operational");
  const [triggerType, setTriggerType] = useState("transactional");
  const [includeEmail, setIncludeEmail] = useState(true);
  const [includeSms, setIncludeSms] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!key.trim() || !name.trim()) {
      toast.error("Key and name are required");
      return;
    }
    setSaving(true);
    try {
      const { data: tpl, error } = await (supabase as any)
        .from("message_templates")
        .insert({
          key: key.trim(),
          name: name.trim(),
          description: description.trim() || null,
          category,
          trigger_type: triggerType,
          is_active: false,
        })
        .select()
        .single();
      if (error) throw error;

      const channels: any[] = [];
      if (includeEmail) channels.push({ template_id: tpl.id, channel: "email", is_enabled: false });
      if (includeSms) channels.push({ template_id: tpl.id, channel: "sms", is_enabled: false });
      if (channels.length) {
        await (supabase as any).from("message_template_channels").insert(channels);
      }

      queryClient.invalidateQueries({ queryKey: ["message-templates"] });
      toast.success("Template created");
      navigate(`/admin/messages/templates/${tpl.id}`);
    } catch (err: any) {
      toast.error("Create failed: " + (err?.message || "unknown"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface-page min-h-screen">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <Link
          to="/admin/messages/templates"
          className="inline-flex items-center gap-1 text-body-sm text-cyan hover:text-cyan-hover mb-3"
        >
          ← Back to templates
        </Link>
        <h1 className="text-display-sm text-ink-primary font-display mb-6">New Template</h1>

        <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-6 space-y-4">
          <div>
            <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
              Key <span className="text-status-danger">*</span>
            </label>
            <input
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="e.g. event_registration_confirmation"
              className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-md text-ink-primary font-mono"
            />
            <p className="text-caption text-ink-tertiary mt-1">
              Unique identifier used in code. Lowercase, snake_case.
            </p>
          </div>

          <div>
            <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
              Name <span className="text-status-danger">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Registration Confirmation"
              className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-md text-ink-primary"
            />
          </div>

          <div>
            <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this template is used for..."
              className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-md text-ink-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-md text-ink-primary"
              >
                {[
                  "registration",
                  "payment",
                  "invitation",
                  "operational",
                  "authentication",
                  "admin_notification",
                  "campaign",
                  "utility",
                  "roster",
                ].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-body-sm text-ink-primary font-medium mb-1.5">
                Trigger Type
              </label>
              <select
                value={triggerType}
                onChange={(e) => setTriggerType(e.target.value)}
                className="w-full bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-md text-ink-primary"
              >
                <option value="transactional">Transactional</option>
                <option value="broadcast">Broadcast</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-body-sm text-ink-primary font-medium mb-2">Channels</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeEmail}
                  onChange={(e) => setIncludeEmail(e.target.checked)}
                />
                <span className="text-body-md text-ink-primary">📧 Email</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeSms}
                  onChange={(e) => setIncludeSms(e.target.checked)}
                />
                <span className="text-body-md text-ink-primary">💬 SMS</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-stroke-subtle">
            <Link
              to="/admin/messages/templates"
              className="bg-surface-page border border-stroke-subtle hover:border-stroke-strong px-4 py-2 rounded-token-md text-body-sm text-ink-primary transition-colors"
            >
              Cancel
            </Link>
            <button
              type="button"
              onClick={handleCreate}
              disabled={saving || !key.trim() || !name.trim()}
              className="bg-cyan text-cyan-foreground hover:bg-cyan-hover disabled:opacity-50 px-5 py-2 rounded-token-md text-body-sm font-medium transition-colors"
            >
              {saving ? "Creating..." : "Create Template"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Edit Page ----------
export default function MessageTemplateEditPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = templateId === "new";

  const { data: template, isLoading } = useQuery({
    queryKey: ["message-template", templateId],
    enabled: !isNew && !!templateId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("message_templates")
        .select(`
          id, key, name, description, category, trigger_type, is_active, organization_id, trigger_events,
          channels:message_template_channels(
            id, channel, is_enabled, subject, body_html, body_text,
            sms_body, push_title, push_body, version, last_modified_at
          )
        `)
        .eq("id", templateId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: variables = [] } = useQuery({
    queryKey: ["template-variables", templateId],
    enabled: !isNew && !!templateId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("message_template_variables")
        .select("*")
        .eq("template_id", templateId)
        .order("sort_order");
      return data || [];
    },
  });

  const channels = template?.channels || [];
  const [activeChannel, setActiveChannel] = useState<string>("email");

  useEffect(() => {
    if (channels.length && !channels.find((c: any) => c.channel === activeChannel)) {
      setActiveChannel(channels[0].channel);
    }
  }, [template?.id]);

  useEffect(() => {
    document.title = template?.name
      ? `${template.name} | Communication Center`
      : "Communication Center | RLevents";
  }, [template?.name]);

  const handleToggleActive = async () => {
    if (!template) return;
    const { error } = await (supabase as any)
      .from("message_templates")
      .update({ is_active: !template.is_active })
      .eq("id", template.id);
    if (error) {
      toast.error("Update failed");
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["message-template", templateId] });
    queryClient.invalidateQueries({ queryKey: ["message-templates"] });
    toast.success(template.is_active ? "Template set inactive" : "Template set active");
  };

  if (isNew) return <NewTemplateForm />;

  if (isLoading) {
    return (
      <div className="bg-surface-page min-h-screen px-6 py-8">
        <div className="max-w-7xl mx-auto text-body-md text-ink-tertiary">Loading...</div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="bg-surface-page min-h-screen px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/admin/messages/templates"
            className="inline-flex items-center gap-1 text-body-sm text-cyan hover:text-cyan-hover mb-3"
          >
            ← Back to templates
          </Link>
          <p className="text-body-md text-ink-tertiary">Template not found.</p>
        </div>
      </div>
    );
  }

  const currentChannel = channels.find((c: any) => c.channel === activeChannel);

  return (
    <div className="bg-surface-page min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <header className="mb-6">
          <Link
            to="/admin/messages/templates"
            className="inline-flex items-center gap-1 text-body-sm text-cyan hover:text-cyan-hover mb-3"
          >
            ← Back to templates
          </Link>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-display-sm text-ink-primary font-display">{template.name}</h1>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <span className="text-caption text-ink-tertiary uppercase tracking-wide">
                  Key: <code className="text-ink-secondary">{template.key}</code>
                </span>
                <span className="text-caption text-ink-tertiary uppercase tracking-wide">
                  Type: {template.trigger_type}
                </span>
                <span className="text-caption text-ink-tertiary uppercase tracking-wide capitalize">
                  Category: {template.category}
                </span>
                {!template.is_active && (
                  <span className="text-caption bg-status-danger-subtle text-status-danger px-2 py-0.5 rounded-token-sm uppercase tracking-wide">
                    Inactive
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleToggleActive}
              className="bg-surface-card border border-stroke-subtle hover:border-cyan px-4 py-2 rounded-token-md text-body-sm text-ink-primary transition-colors whitespace-nowrap"
            >
              {template.is_active ? "Set Inactive" : "Set Active"}
            </button>
          </div>
        </header>

        {channels.length === 0 ? (
          <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-12 text-center text-body-md text-ink-tertiary">
            No channels configured for this template.
          </div>
        ) : (
          <>
            <div className="border-b border-stroke-subtle mb-6 flex gap-1">
              {channels.map((c: any) => (
                <button
                  key={c.channel}
                  type="button"
                  onClick={() => setActiveChannel(c.channel)}
                  className={`px-4 py-3 -mb-px border-b-2 text-body-md transition-colors ${
                    activeChannel === c.channel
                      ? "border-cyan text-ink-primary font-medium"
                      : "border-transparent text-ink-tertiary hover:text-ink-secondary"
                  }`}
                >
                  {c.channel === "email" ? "📧 Email" : c.channel === "sms" ? "💬 SMS" : "🔔 Push"}
                  {!c.is_enabled && (
                    <span className="ml-2 text-caption text-status-warning">(off)</span>
                  )}
                </button>
              ))}
            </div>

            {currentChannel &&
              (activeChannel === "email" ? (
                <EmailChannelEditor
                  template={template}
                  channel={currentChannel}
                  variables={variables}
                />
              ) : activeChannel === "sms" ? (
                <SmsChannelEditor
                  template={template}
                  channel={currentChannel}
                  variables={variables}
                />
              ) : (
                <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-6 text-body-md text-ink-tertiary">
                  Push not implemented
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  );
}
